"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext } from "react";
import { useFormContext, useController } from "react-hook-form";
import { DatePickerInput, type DatePickerCaptionLayout } from "@/components/molecules/date-picker-input/DatePickerInput";
import { FormFieldContext, FormItem, FormMessage } from "@/components/ui/form";
import { FormLabel, type FormLabelSize } from "@/components/atoms/Form/FormLabel";
import { cn } from "@/lib/utils";
type FormDatePickerProps = {
  name: string;
  label?: string;
  labelSize?: FormLabelSize;
  size?: "md" | "sm";
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  disableDates?: (date: Date) => boolean;
  title?: string;
  captionLayout?: DatePickerCaptionLayout;
  startMonth?: Date;
  endMonth?: Date;
  plainInput?: boolean;
};

/**
 * RHF wrapper around `DatePickerInput`. Use this when the date lives in a
 * react-hook-form. For controlled, form-library-free usage, use
 * `DatePickerInput` directly.
 */
export function FormDatePicker(t0) {
  const $ = _c(34);
  const {
    name,
    label,
    labelSize,
    size,
    required,
    placeholder,
    disabled,
    disableDates,
    title,
    captionLayout,
    startMonth,
    endMonth,
    plainInput
  } = t0;
  const {
    control
  } = useFormContext();
  const isNested = useContext(FormFieldContext)?.name === name;
  let t1;
  if ($[0] !== control || $[1] !== name) {
    t1 = {
      name,
      control
    };
    $[0] = control;
    $[1] = name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    field,
    fieldState: t2
  } = useController(t1);
  const {
    error
  } = t2;
  const t3 = !!error;
  let t4;
  if ($[3] !== captionLayout || $[4] !== disableDates || $[5] !== disabled || $[6] !== endMonth || $[7] !== field.onChange || $[8] !== field.value || $[9] !== placeholder || $[10] !== plainInput || $[11] !== size || $[12] !== startMonth || $[13] !== t3 || $[14] !== title) {
    t4 = <DatePickerInput value={field.value} onChange={field.onChange} placeholder={placeholder} disabled={disabled} disableDates={disableDates} title={title} captionLayout={captionLayout} startMonth={startMonth} endMonth={endMonth} plainInput={plainInput} size={size} hasError={t3} />;
    $[3] = captionLayout;
    $[4] = disableDates;
    $[5] = disabled;
    $[6] = endMonth;
    $[7] = field.onChange;
    $[8] = field.value;
    $[9] = placeholder;
    $[10] = plainInput;
    $[11] = size;
    $[12] = startMonth;
    $[13] = t3;
    $[14] = title;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const input = t4;
  if (!label) {
    return input;
  }
  let t5;
  if ($[16] !== name) {
    t5 = {
      name
    };
    $[16] = name;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const t6 = size === "sm" ? "gap-1.5" : "gap-2";
  let t7;
  if ($[18] !== t6) {
    t7 = cn("flex flex-col", t6);
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const t8 = labelSize ?? size;
  let t9;
  if ($[20] !== label || $[21] !== required || $[22] !== t8) {
    t9 = <FormLabel required={required} size={t8} data-sentry-element="FormLabel" data-sentry-source-file="FormDatePicker.tsx">{label}</FormLabel>;
    $[20] = label;
    $[21] = required;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== isNested) {
    t10 = !isNested && <FormMessage />;
    $[24] = isNested;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== input || $[27] !== t10 || $[28] !== t7 || $[29] !== t9) {
    t11 = <FormItem className={t7} data-sentry-element="FormItem" data-sentry-source-file="FormDatePicker.tsx">{t9}{input}{t10}</FormItem>;
    $[26] = input;
    $[27] = t10;
    $[28] = t7;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t5) {
    t12 = <FormFieldContext.Provider value={t5} data-sentry-element="FormFieldContext.Provider" data-sentry-component="FormDatePicker" data-sentry-source-file="FormDatePicker.tsx">{t11}</FormFieldContext.Provider>;
    $[31] = t11;
    $[32] = t5;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}